import * as vp from 'vopay';
import moment from 'moment';
import { FunctionComponent, useEffect, useState, useMemo } from 'react';
import useFetchPortalVideos from '../../../../hooks/useApi/useFetchPortalVideos';
import VideoSearchForm from './VideoSearchForm';
import VideoSearchResult from './VideoSearchResult';
import styles from './VideoContainer.module.scss';

type FC = FunctionComponent;

type VideoPlayerProps = {
  video: vp.VideoObject;
};

const { Box, Title, BackButton } = vp;

const videoTitleOrder = [
  'Referral Partner Account',
  'VAR Partner Account',
  'VAR Payfac Partner Account',
  'Creating VoPay Accounts',
  'Partner Account Reports',
  'Introduction',
  'Sending a Single Payment',
  'Request a Single Collection',
  'Scheduled Transactions',
  'Bulk Payment',
  'Split Payments',
  'VoPay Verify',
  'Transaction Approvals',
  'Accounting Software Integrations',
  'One-Time and Recurring Reports',
  'Application and Agreement Guide',
  'ID Verification',
  'Submit a Ticket',
];

const VideoContainer: FC = () => {
  const [activeTab, setActiveTab] = useState('Referral Partner Account');
  const [isDropdownSelected, setIsDropdownSelected] = useState(false);
  const [hasSearchResult, setHasSearchResult] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredByTabLists, setFilteredByTabLists] = useState<vp.VideoObject[]>([]);
  const [filteredByKeyWordLists, setFilteredByKeyWordLists] = useState<{ [key: string]: vp.VideoObject }>({});
  const [selectedVideo, setSelectedVideo] = useState<vp.VideoObject>({
    title: '',
    url: '',
    thumbnail: '',
    category: '',
    length: 0,
    lastModified: '',
  });
  const [videos, fetchPortalVideos] = useFetchPortalVideos({});

  useEffect(() => {
    fetchPortalVideos({
      IsPartner: false,
    });
  }, []);

  useEffect(() => {
    if (Object.values(videos.VideoList || {}).length >= 1) {
      setActiveTab(videos.VideoList[0]?.category || '');
    }
  }, [videos]);

  useEffect(() => {
    const sortedFilteredByTitles = Object.values(videos.VideoList).sort((a, b) => {
      const indexA = videoTitleOrder.indexOf(a.title);
      const indexB = videoTitleOrder.indexOf(b.title);

      const adjustedIndexA = indexA === -1 ? videoTitleOrder.length : indexA;
      const adjustedIndexB = indexB === -1 ? videoTitleOrder.length : indexB;

      return adjustedIndexA - adjustedIndexB;
    });

    setFilteredByTabLists(sortedFilteredByTitles);
  }, [activeTab, selectedVideo, videos]);

  useEffect(() => {
    if (!isDropdownSelected) {
      setSelectedVideo(
        Object.values(videos.VideoList || {}).filter((video) => video.title?.includes('Referral Partner Account'))[0],
      );
    }
  }, [activeTab, videos]);

  useEffect(() => {
    setHasSearchResult(false);
  }, [selectedVideo]);

  useEffect(() => {
    if (searchTerm === '') setHasSearchResult(false);
  }, [searchTerm]);

  const getSelectedVideo = (video: vp.VideoObject) => {
    setActiveTab(video.category || '');
    setSelectedVideo(video);
    setIsDropdownSelected(true);
    setHasSearchResult(false);
    setSearchTerm('');
  };

  const VideoPlayer = ({ video }: VideoPlayerProps) => (
    <>
      <h4>{video?.title}</h4>
      <div className={styles.videoPlayer}>
        <video controls width="100%" height="100%" muted autoPlay={false} poster="" controlsList="nodownload">
          <source src={video?.url} type="video/mp4" />
          Sorry, your browser doesn&#39;t support embedded videos.
        </video>
      </div>
    </>
  );

  const MemoizedVideoPlayer = useMemo(() => <VideoPlayer video={selectedVideo} />, [selectedVideo]);

  return (
    <Box flx="col">
      <Box mb="lg" jc="fs" ai="center" gap="common">
        <BackButton variant="arrow" backButtonEnabled />
        <Title title="Video Tutorials" />
      </Box>
      <Box flx="row" jc="sb" ai="center" mt="md" gap="md">
        <Box lineHeight="25px">
          Explore step-by-step videos designed to guide you through the VoPay Portal with ease.
        </Box>
        <VideoSearchForm
          videos={videos.VideoList}
          filteredByKeyWordLists={filteredByKeyWordLists}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          setFilteredByKeyWordLists={setFilteredByKeyWordLists}
          setHasSearchResult={setHasSearchResult}
          getSelectedVideo={getSelectedVideo}
        />
      </Box>

      {Object.values(videos.VideoList || {}).length >= 1 && (
        <div className={styles.guideContainer}>
          <div className={styles.guideListsContainer}>
            <div className={styles.guideScrollableList}>
              {filteredByTabLists.map((video) => (
                <button
                  key={video.url}
                  type="button"
                  className={[styles.guideList, styles[getActiveClass(video.title)]].join(' ')}
                  onClick={() => {
                    setSelectedVideo(video);
                  }}
                >
                  <div className={styles.thumbnail}>
                    <div className={styles.length}>{String(video.length)?.replace('.', ':')}</div>
                    <img src={video.thumbnail} alt="" />
                  </div>
                  <div className={styles.details}>
                    <h5>{video.title}</h5>
                    <span>{moment(video.lastModified).format('MMMM D, YYYY')}</span>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {hasSearchResult ? (
            <VideoSearchResult
              searchTerm={searchTerm}
              filteredByKeyWordLists={filteredByKeyWordLists}
              getSelectedVideo={getSelectedVideo}
            />
          ) : (
            <div className={styles.guide}>{selectedVideo?.title !== '' && MemoizedVideoPlayer}</div>
          )}
        </div>
      )}
    </Box>
  );

  function getActiveClass(title: string) {
    return title === selectedVideo.title && !hasSearchResult ? 'active' : '';
  }
};

export default VideoContainer;
